/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './chain-counts-page.component.html';
import {NetworkInfoResource} from '../../../components/api/resources/resource/network.resource';
import {createBufferedFlag, readBufferedFlag} from '../../common/util/template-state';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

const CHAIN_LEVELS = [
  {value: 'ADVERTISER', label: 'Advertiser'},
  {value: 'ORDER', label: 'Order'},
  {value: 'LINE_ITEM', label: 'Line Item'},
  {value: 'CREATIVE', label: 'Creative'}
];

@Component({
  standalone: false,
  selector: 'adn-chain-counts-page',
  template
})
export class ChainCountsPageComponent implements OnInit, OnDestroy {
  readonly chainLevels = CHAIN_LEVELS;

  startLevel = 'ADVERTISER';
  endLevel = 'LINE_ITEM';
  delivering = true;
  readyToDeliver = true;

  loaded = true;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  searched = false;
  pageSize = 50;
  currentPage = 1;
  pageQuery: any = {$resolved: true};
  results: any[] = [];
  totalCount = 0;

  constructor(
    @Inject(NetworkInfoResource) private readonly NetworkInfo: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  ngOnInit(): void {
    this.runQuery(true);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  startLevelOptions(): any[] {
    return _.filter(this.chainLevels, (level) => level.value !== 'CREATIVE');
  }

  endLevelOptions(): any[] {
    const startIndex = _.findIndex(this.chainLevels, {value: this.startLevel});
    return _.filter(this.chainLevels, (_level, index) => index > startIndex);
  }

  onStartLevelChange(): void {
    const endOptions = this.endLevelOptions();
    if (!_.find(endOptions, {value: this.endLevel})) {
      this.endLevel = _.get(endOptions, [0, 'value']);
    }
  }

  runQuery(resetPage?: boolean): void {
    if (!this.startLevel || !this.endLevel) {
      return;
    }
    if (resetPage) {
      this.currentPage = 1;
    }
    this.loaded = false;
    this.pageQuery = this.NetworkInfo.chainCounts({
      startLevel: this.startLevel,
      endLevel: this.endLevel,
      delivering: this.delivering,
      readyToDeliver: this.readyToDeliver,
      page: this.currentPage,
      pageSize: this.pageSize
    });
    this.pageQuery.$promise.then((data: any) => {
      this.results = data.results || [];
      this.totalCount = data.totalCount || 0;
      this.searched = true;
      this.loaded = true;
    }, () => {
      this.results = [];
      this.totalCount = 0;
      this.searched = true;
      this.loaded = true;
    });
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.runQuery(false);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.currentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.pageSize * (this.currentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.pageSize * this.currentPage, this.totalCount || 0);
  }

  levelLabel(value: string): string {
    return _.get(_.find(this.chainLevels, {value}), 'label', value);
  }

  countColumnLabel(): string {
    return this.levelLabel(this.endLevel) + ' count';
  }

  rowStateName(): string {
    switch (this.startLevel) {
      case 'ADVERTISER':
        return 'app.adv.advertisers.advertiser';
      case 'ORDER':
        return 'app.adv.orders.order';
      case 'LINE_ITEM':
        return 'app.adv.line-items.line-item';
      default:
        return '';
    }
  }
}
